// URL sync — keep the expanded-dimension state mirrored to the URL.
//
// shiny.router uses hash-based routing (/#!/simulation), so we tuck our
// own query string after the hash:
//   /#!/simulation?dim=inequality
// That way shiny.router still resolves the route correctly while the
// expanded-dimension state rides along in the same fragment.

import { useEffect } from "react";
import { useResultsStore } from "./results_store";
import type { DimensionKey } from "../lib/dimensions";
import { dimensionByKey } from "../lib/dimensions";

function parseHashQuery(): URLSearchParams {
  const hash = window.location.hash;
  const qIndex = hash.indexOf("?");
  if (qIndex === -1) return new URLSearchParams();
  return new URLSearchParams(hash.slice(qIndex + 1));
}

function isDimension(v: string | null): v is DimensionKey {
  return v !== null && v in dimensionByKey;
}

/** Read URL -> store on mount; write store -> URL on changes. */
export function useUrlSync(): void {
  const expandedDimension = useResultsStore((s) => s.expandedDimension);
  const setExpandedDimension = useResultsStore((s) => s.setExpandedDimension);

  // Hydrate from URL on mount.
  useEffect(() => {
    const params = parseHashQuery();
    const urlDim = params.get("dim");
    if (isDimension(urlDim)) setExpandedDimension(urlDim);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror store -> URL on changes.
  useEffect(() => {
    const hash = window.location.hash;
    const qIndex = hash.indexOf("?");
    const route = qIndex === -1 ? hash : hash.slice(0, qIndex);

    const params = new URLSearchParams();
    if (expandedDimension) params.set("dim", expandedDimension);

    const qs = params.toString();
    const newHash = qs ? `${route}?${qs}` : route;
    if (newHash !== hash) {
      window.history.replaceState(null, "", newHash || "#");
    }
  }, [expandedDimension]);
}
